<template>
  <nx-dashboard-card class="nx-dashboard-shortcuts-card" :title="$t('message.dashboard.shortcuts.title')">
    <template v-slot:content>
      <v-list>
        <v-list-tile @click="openDialog('create')">
          <v-list-tile-avatar>
            <v-icon>add</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-action v-text="$t('message.dashboard.shortcuts.options.create')"></v-list-tile-action>
        </v-list-tile>
        <v-list-tile @click="openDialog('manage')">
          <v-list-tile-avatar>
            <v-icon>edit</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-action v-text="$t('message.dashboard.shortcuts.options.manage')"></v-list-tile-action>
        </v-list-tile>
      </v-list>
    </template>

    <template v-slot:body>
      <div class="layout wrap px-3 pb-3 ma-0 row wrap shortcuts-list" v-if="shortcuts.length > 0">
        <v-flex d-flex xs4 sm3 md4 lg3 xl2
                v-for="shortcut in shortcuts"
                :key="shortcut.id">
          <div class="nx-dashboard-shortcut">
            <component :is="shortcut.type"
                       v-bind="shortcut"
                       :text="translate(shortcut.text)"
            ></component>
          </div>
        </v-flex>
      </div>

      <v-layout v-else>
        <v-flex>
          <nx-dashboard-info-message
            :title="$t('message.dashboard.shortcuts.wizard.error.callToAction')"
            :text="$t('message.dashboard.shortcuts.wizard.error.emptyShortcutsText')"
          />
        </v-flex>
      </v-layout>

      <nx-dashboard-shortcuts-manage-dialog
        :card-id="id"
        v-if="dialogState.manage.render"
        :value="dialogState.manage.active"
        @input:dialog="closeDialog('manage')"
      />
      <nx-dashboard-shortcuts-wizard
        :card-id="id"
        :wizard-config="wizardConfigAsObject"
        v-if="dialogState.create.render"
        :value="dialogState.create.active"
        @input:dialog="closeDialog('create')"
      />
    </template>

  </nx-dashboard-card>
</template>

<script>
import NxDashboardCard from '@/modules/dashboard/components/DashboardCard';
import NxDashboardShortcutsCreateEntity from '@/modules/dashboard/shortcuts/components/DashboardShortcutsCreateEntity';
import NxDashboardShortcutsLink from '@/modules/dashboard/shortcuts/components/DashboardShortcutsLink';
import { translateCandidates } from '@/utils';
import DashboardCard from '@/modules/dashboard/mixins/dashboard-card';
import ManageDialogs, { initDialog } from '@/mixins/manage-dialogs';
import NxDashboardShortcutsManageDialog from '@/modules/dashboard/shortcuts/components/DashboardShortcutsManageDialog';
import NxDashboardShortcutsWizard from '@/modules/dashboard/shortcuts/components/DashboardShortcutsWizard';
import NxSavedSearchesWizard from '@/modules/saved-searches/components/SavedSearchesWizard.vue';
import NxDashboardInfoMessage from '@/modules/dashboard/components/DashboardInfoMessage.vue';

export default {
  name: 'nx-dashboard-shortcuts-card',
  mixins: [DashboardCard, ManageDialogs],
  components: {
    NxDashboardInfoMessage,
    NxSavedSearchesWizard,
    DashboardCreateEntityShortcut: NxDashboardShortcutsCreateEntity,
    DashboardLinkShortcut: NxDashboardShortcutsLink,
    NxDashboardCard,
    NxDashboardShortcutsManageDialog,
    NxDashboardShortcutsWizard,
  },
  props: {
    wizardConfig: {
      type: [Object, Array],
      validator: (value) => {
        if (Array.isArray(value)) {
          // API serializes an empty object as an array, so we allow an empty array as value.
          return value.length === 0;
        }
        return typeof value === 'object';
      },
      default: () => ({}),
    },
  },
  data() {
    return {
      dialogState: {
        create: initDialog(),
        manage: initDialog(),
      },
    };
  },
  computed: {
    wizardConfigAsObject() {
      if (Array.isArray(this.wizardConfig)) return {};
      return this.wizardConfig;
    },
    shortcuts() {
      return this.safeStaffPreferences.shortcuts || [];
    },
  },
  methods: {
    translate(translatable) {
      return translateCandidates(
        [
          `message.${translatable}`,
          translatable,
        ],
        translatable,
      );
    },
    saveShortcuts(shortcuts) {
      this.$store.dispatch('dashboard/SAVE_SHORTCUTS', {
        cardId: this.id,
        shortcuts,
      });
    },
  },
};
</script>

<style lang="stylus">
@import '../../../assets/stylus/variables.styl'

.nx-dashboard-shortcuts-card {

  .edit-icon {
    opacity: .7;
  }

  .nx-dashboard-shortcut {
    padding: 8px;

    &__content {
      display: flex;
      justify-content: center;
      align-items: center;

      .v-btn {
        height: inherit;
        aspect-ratio: 4 / 3;
        padding: 8px 12px;
        align-items: flex-start;

        .v-btn__content {
          flex-direction: column;
          white-space: break-spaces;
          text-align: center;
          text-transform: capitalize;
        }

        .v-card__text {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          padding: 0;
          margin: 8px 0 0 0;
          overflow: hidden;
        }
      }
    }

    &.editing {
      cursor: pointer;
      border-radius: 8px;
      border: 1px dashed $nx-neutral-300;

      .theme--light.v-btn.v-btn--disabled {
        color: inherit !important;
      }
    }

    &.sortable-ghost {
      cursor: grab;
      opacity: .2;
    }
  }
}
</style>
